import React, { useEffect, useState } from 'react'
import {
  WithStyles,
  withStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles'
import { SiteCommentResponseModel } from 'api/apiservice'
import { getSiteClient } from 'api/client'
import { Site } from 'store/Site'
import CommentsTable from './commentsTable'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  CircularProgress,
} from '@material-ui/core'
import Comments from '.'
import { SaveTwoTone } from '@material-ui/icons'
import { ConfirmDialog } from 'components/misc/ConfirmDialog'
import Snack from 'components/misc/Snack'
import { set } from 'date-fns'

interface Props {
  site: Site | undefined
  accessToken: string
  tenantId: number
  userId: string | undefined
  userEmail: string
}

const styles = (theme: Theme) =>
  createStyles({
    value: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      width: 'auto',
    },
    dialogPaperWidthSm: {
      maxWidth: 'unset',
      minWidth: '80vh',
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(2),
    },
  })

type AllProps = Props & WithStyles<typeof styles>

type Color = 'error' | 'info' | 'success' | 'warning'

interface SnackMessage {
  color: Color
  message: string
}

const SiteComments: React.FC<AllProps> = ({
  site,
  accessToken,
  tenantId,
  userId,
  userEmail,
  classes,
}) => {
  const [comments, setComments] = useState<SiteCommentResponseModel[]>([])
  const [loading, setLoading] = useState(false)
  const [openModalComment, setOpenModalComment] = useState(false)
  const [openDeleteComment, setOpenDeleteComment] = useState(false)
  const [siteUpdating, setSiteUpdating] = useState(false)
  const [isEdited, setIsEdited] = useState(false)
  const [comment, setComment] = useState<SiteCommentResponseModel | undefined>(
    undefined
  )
  const [isEditComment, setIsEditComment] = useState(false)
  const [snackMessage, setSnackMessage] = useState<SnackMessage | undefined>(
    undefined
  )
  const [openModalViewAll, setOpenModalViewAll] = useState(false)

  const siteAPI = getSiteClient(accessToken, tenantId.toString(), userId)

  const loadMore = async () => {
    if (!site?.id) return
    setLoading(true)
    try {
      const response = await siteAPI.getSiteComments(site.id, tenantId)
      setComments(response.result)
    } catch (error) {
      console.error('Error loading comments:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (comment: SiteCommentResponseModel) => {
    setOpenDeleteComment(true)
    setComment(comment)
    setIsEditComment(false)
  }

  const onCloseDelete = async (ok: boolean) => {
    if (ok && comment?.id) {
      setLoading(true)
      try {
        await siteAPI.delete(comment.id, tenantId)
        setSnackMessage({
          color: 'success',
          message: 'Comment deleted successfully',
        })
        await loadMore()
      } catch (error) {
        setSnackMessage({
          color: 'error',
          message: `Error deleting comment: ${error}`,
        })
        console.error('Error deleting comment:', error)
      } finally {
        setLoading(false)
      }
    }
    setComment(undefined)
    setIsEditComment(false)
    setOpenDeleteComment(false)
  }

  const handleAdd = () => {
    setOpenModalComment(true)
    setComment(undefined)
    setIsEditComment(false)
  }

  const handleSaveComment = async () => {
    if (!comment) return
    setSiteUpdating(true)
    try {
      if (isEditComment) {
        await siteAPI.editSiteComment(comment.id!, comment, tenantId)
        setSnackMessage({
          color: 'success',
          message: `Comment updated successfully`,
        })
      } else {
        comment.siteId = site?.id
        comment.userEmail = userEmail
        comment.createdAt = new Date()
        comment.updatedAt = new Date()
        await siteAPI.addSiteCommment(comment, tenantId)
        setSnackMessage({
          color: 'success',
          message: `Comment added successfully`,
        })
      }
      setIsEdited(false)
      setOpenModalComment(false)
      await loadMore()
    } catch (error) {
      setSnackMessage({
        color: 'error',
        message: `Error saving comment: ${error}`,
      })
      console.error('Error saving comment:', error)
    } finally {
      setSiteUpdating(false)
    }
  }

  const handleEdit = (comment: SiteCommentResponseModel) => {
    setOpenModalComment(true)
    setIsEditComment(true)
    setComment(comment)
  }
  const handleCommentChange = (updated: string | SiteCommentResponseModel) => {
    let isValid = false

    if (typeof updated === 'string') {
      const sanitized = updated.replace(/\r?\n|\r/g, ' ')
      const newComment = new SiteCommentResponseModel()
      newComment.comment = sanitized
      newComment.siteId = site?.id
      newComment.userEmail = userEmail
      newComment.createdAt = new Date()
      newComment.updatedAt = new Date()
      setComment(newComment)

      isValid = sanitized.trim().length > 0 && sanitized.length <= 1024
    } else {
      const sanitized = updated.comment?.replace(/\r?\n|\r/g, ' ') || ''
      updated.comment = sanitized
      setComment(updated)

      isValid = sanitized.trim().length > 0 && sanitized.length <= 1024
    }

    setIsEdited(isValid)
  }

  const handleViewAll = () => {
    setOpenModalViewAll(true)
  }

  const closeSiteStatusDialog = () => {
    setOpenModalComment(false)
    setIsEdited(false)
    setComment(undefined)
    setIsEditComment(false)
  }

  useEffect(() => {
    if (site?.id) {
      loadMore()
    }
  }, [site?.id])

  return (
    <>
      {loading && (
        <div className={classes.loaderContainer}>
          <CircularProgress />
        </div>
      )}

      <CommentsTable
        comments={comments}
        loading={loading}
        userEmail={userEmail}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        onShowMore={handleViewAll}
      />

      {openModalComment && (
        <Dialog
          disableEscapeKeyDown
          open
          classes={{ paperWidthSm: classes.dialogPaperWidthSm }}
        >
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Comments
                site={site!}
                accessToken={accessToken}
                tenantId={tenantId}
                userId={userId!}
                userEmail={userEmail}
                comment={comment}
                onChange={handleCommentChange}
              />
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={siteUpdating}
              onClick={closeSiteStatusDialog}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              disabled={!isEdited || siteUpdating}
              variant="contained"
              color="primary"
              startIcon={<SaveTwoTone />}
              onClick={handleSaveComment}
            >
              {siteUpdating ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openModalViewAll && (
        <Dialog
          disableEscapeKeyDown
          open
          classes={{ paperWidthSm: classes.dialogPaperWidthSm }}
        >
          <DialogContent dividers>
            <Grid container spacing={3}>
              <CommentsTable
                comments={comments}
                loading={loading}
                userEmail={userEmail}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAdd={handleAdd}
                onShowMore={handleViewAll}
                ignoreLimit={true}
              />
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenModalViewAll(false)
              }}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDeleteComment && (
        <ConfirmDialog title="Confirmation" onAction={onCloseDelete}>
          Are you sure to delete this comment?
        </ConfirmDialog>
      )}

      {snackMessage && (
        <Snack
          autoHideDuration={2000}
          message={snackMessage.message}
          severity={snackMessage.color}
          onClose={() => {
            setSnackMessage(undefined)
            setOpenDeleteComment(false)
            setOpenModalComment(false)
            setIsEdited(false)
            setComment(undefined)
            setIsEditComment(false)
            setLoading(false)
            setSiteUpdating(false)
          }}
        />
      )}
    </>
  )
}

export default withStyles(styles)(SiteComments)
